import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../components/ui/dialog";
import {
  FileText,
  PenTool,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Eraser,
  Send
} from "lucide-react";
import { toast } from "sonner";

export default function SignDocumentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [signatureRequest, setSignatureRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    fetchSignatureRequest();
  }, [id]);

  useEffect(() => {
    if (canvasRef.current) {
      initCanvas();
    }
  }, [signatureRequest]);

  const fetchSignatureRequest = async () => {
    try {
      const response = await api.get(`/signatures/${id}`);
      setSignatureRequest(response.data);
    } catch (error) {
      toast.error("Demande de signature non trouvée");
      navigate("/signatures");
    } finally {
      setLoading(false);
    }
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#1a1a2e";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const coords = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const coords = getCoords(e);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSubmit = async () => {
    if (!hasSignature) {
      toast.error("Veuillez dessiner votre signature");
      return;
    }

    setSubmitting(true);
    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL("image/png");

      // Get the first field for this user
      const myField = signatureRequest.fields[0];

      await api.post(`/signatures/${id}/sign`, {
        signature_data: signatureData,
        field_id: myField?.id || "default"
      });

      toast.success("Document signé avec succès !");
      setShowConfirm(false);
      navigate("/signatures");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Erreur lors de la signature"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!signatureRequest) {
    return null;
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="sign-document-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/signatures")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Signer le document
          </h1>
          <p className="text-muted-foreground mt-1">
            {signatureRequest.document_name}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Document Info */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Informations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Document</p>
              <p className="font-medium">{signatureRequest.document_name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Envoyé par</p>
              <p className="font-medium">{signatureRequest.sender_name}</p>
            </div>
            {signatureRequest.message && (
              <div>
                <p className="text-sm text-muted-foreground">Message</p>
                <p className="text-sm italic">"{signatureRequest.message}"</p>
              </div>
            )}
            <div>
              <p className="text-sm text-muted-foreground mb-2">Signataires</p>
              <div className="space-y-2">
                {signatureRequest.recipients.map((recipient) => (
                  <div
                    key={recipient.user_id}
                    className="flex items-center justify-between text-sm"
                  >
                    <span>{recipient.full_name}</span>
                    <Badge
                      variant="secondary"
                      className={
                        recipient.status === "signed"
                          ? "status-signed"
                          : "status-pending"
                      }
                    >
                      {recipient.status === "signed" ? (
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                      ) : null}
                      {recipient.status === "signed" ? "Signé" : "En attente"}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Signature Pad */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              Votre signature
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Dessinez votre signature dans le cadre ci-dessous
            </p>

            <div className="border-2 border-dashed rounded-lg p-2 bg-white">
              <canvas
                ref={canvasRef}
                className="w-full h-48 cursor-crosshair touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                data-testid="signature-canvas"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={clearCanvas}
                className="flex-1"
                data-testid="clear-signature-btn"
              >
                <Eraser className="w-4 h-4 mr-2" />
                Effacer
              </Button>
              <Button
                onClick={() => setShowConfirm(true)}
                disabled={!hasSignature}
                className="flex-1 btn-active"
                data-testid="confirm-signature-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Valider la signature
              </Button>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              En signant ce document, vous acceptez que votre signature soit
              juridiquement contraignante conformément aux normes eIDAS.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la signature</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Êtes-vous sûr de vouloir signer ce document ? Cette action est
            irréversible et votre signature aura une valeur juridique.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="btn-active"
              data-testid="final-sign-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Signer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
